/* eslint-disable @typescript-eslint/no-explicit-any -- generic */
import { Button, CircularProgress, Stack, Typography } from "@mui/material";
import { useState } from "react";
import type { ErrorInfo } from "remult";
import type * as yup from "yup"; // Import Yup
import { useDialog } from "../contexts/DialogContext";
import FormProvider from "../helpers/forms/FormProvider";
import { useError } from "./ErrorDialog";
import "./FormDialog.scss";
import FormGroup, { type FieldConfig } from "./FormGroup";

type FieldsConfigToValuesType<
  ConfigType extends Record<string, Partial<FieldConfig>>,
> = {
    [k in keyof ConfigType]: ConfigType[k]["type"] extends "checkbox"
    ? boolean
    : ConfigType[k]["type"] extends "number"
    ? number
    : string; // Ensure string is the fallback type
  };

export function useFormDialog() {
  const dialog = useDialog();
  return function <
    ConfigType extends Record<string, Partial<FieldConfig>>,
  >(args: {
    loading?: boolean;
    title?: string;
    fields: ConfigType;
    defaultValues: FieldsConfigToValuesType<ConfigType>;
    onOk: (value: FieldsConfigToValuesType<ConfigType>) => void | Promise<void>;
    validationSchema?: yup.Schema<FieldsConfigToValuesType<ConfigType>>;
  }) {
    type T = FieldsConfigToValuesType<ConfigType>;
    return dialog<T>((resolve) => {
      const [state, setState] = useState<T>(args.defaultValues ?? ({} as T));
      const [error, setError] = useState<ErrorInfo<T>>();
      const errorDialog = useError();

      async function ok() {
        try {
          setError(undefined);
          resolve(state);
          await args.onOk(state);
          if (!args.loading) resolve();
        } catch (err) {
          setError(err as ErrorInfo<T>);
          errorDialog({ message: (err as ErrorInfo<T>).message! });
        }
      }

      return (
        <Stack gap="2rem" sx={{ overflowY: "auto" }} maxHeight="60vh">
          <FormProvider
            initialValues={args.defaultValues}
            onSubmit={ok}
            validate={() => {
              try {
                args.validationSchema?.validateSync(state, {
                  abortEarly: false,
                });
                return {};
              } catch (Error: any) {
                if (Error?.inner) {
                  const errors = Error.inner.reduce((acc: any, err: any) => {
                    acc[err.path] = err.message;
                    return acc;
                  }, {});
                  setError({ modelState: errors } as ErrorInfo<T>);

                  return errors;
                }
                throw Error;
              }
            }}
          >
            <Stack>
              <Stack mb="2vh">
                <Typography
                  textAlign="center"
                  mb="0.4rem"
                  fontSize="1.2rem"
                  fontWeight={700}
                >
                  {args.title ?? ""}
                </Typography>
              </Stack>
              <FormGroup
                fields={args.fields}
                state={state as any}
                setState={setState as any}
                error={error}
                setError={setError}
              />
            </Stack>
            <Stack
              flexDirection="row"
              alignItems="center"
              width="100%"
              marginTop="4vh"
            >
              <Button className="save-button" type="submit">
                שמירה
                {args.loading && <CircularProgress />}
              </Button>
            </Stack>
          </FormProvider>
        </Stack>
      );
    });
  };
}
/* eslint-enable @typescript-eslint/no-explicit-any -- generic */
